import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import { ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import {
  DURATION,
  EASE,
  prefersReducedMotion,
  revealDialog,
} from "../../animations/gsapAnimations";
import { useFocusTrap } from "../../hooks/useFocusTrap";
import { assetUrl } from "../../lib/http";
import { CAR_IMAGE } from "./VehicleMedia";
import StudioStage, { STAGE_SUBJECT, StudioSpill } from "./StudioStage";

type VehicleGalleryProps = {
  images: string[];
  alt: string;
};

/* Main frame, thumbnail strip, and a fullscreen viewer. Keyboard drives all
   three: arrows step, Escape closes. Touch gets a swipe.

   The set of frames is not fixed for the life of the component: the detail
   page reuses it when a visitor navigates from one car to another. So the
   position and the failures both belong to the set rather than to the
   gallery, and are dropped when it changes. */
function VehicleGallery({ images, alt }: VehicleGalleryProps) {
  const [index, setIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [failed, setFailed] = useState<Record<number, boolean>>({});

  /* Photographs the console stored arrive as paths on the API's host, pasted
     ones as absolute URLs. Both have to resolve against the API rather than
     against the storefront. */
  const frames = useMemo(() => images.map(assetUrl), [images]);

  /* A new set of frames means frame three of the old one is meaningless, and
     an index past the end of a shorter gallery would render nothing at all.
     Rendered during the render that changed them, not in an effect, so the
     first paint is already correct. */
  const [shown, setShown] = useState(images);
  if (shown !== images) {
    setShown(images);
    setIndex(0);
    setFailed({});
  }

  const mainRef = useRef<HTMLImageElement>(null);
  const lightboxRef = useRef<HTMLDivElement>(null);
  const touchStart = useRef<number | null>(null);

  useFocusTrap(lightboxRef, zoomed);

  const count = images.length;
  const step = useCallback(
    (delta: number) => setIndex((current) => (current + delta + count) % count),
    [count],
  );

  /* A crossfade rather than a slide: the frames are of the same car, so
     sliding implies a spatial relationship that is not there. */
  useLayoutEffect(() => {
    const el = mainRef.current;
    if (!el || prefersReducedMotion()) return;

    const tween = gsap.fromTo(
      el,
      { opacity: 0, scale: 1.02 },
      { opacity: 1, scale: 1, duration: DURATION.fast, ease: EASE },
    );

    return () => {
      tween.kill();
    };
  }, [index]);

  useLayoutEffect(() => {
    if (!zoomed) return;

    /* The viewer arrives the way every dialog does — a settle, not a pop. */
    if (lightboxRef.current) revealDialog(lightboxRef.current);

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setZoomed(false);
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };

    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [zoomed, step]);

  const onTouchEnd = (event: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 45) step(delta < 0 ? 1 : -1);
    touchStart.current = null;
  };

  if (!count) return null;

  return (
    <div className="flex flex-col gap-4">
      {/* ---------- main frame ---------- */}
      <div
        className="group relative aspect-16/10 overflow-hidden rounded-[28px] border border-line shadow-lift"
        onTouchStart={(event) => {
          touchStart.current = event.touches[0].clientX;
        }}
        onTouchEnd={onTouchEnd}
      >
        {/* The room the car stands in — the same one a car still waiting for
            its shoot stands in, so the lighting does not change between a
            photographed car and an unphotographed one. It sits behind the
            frame rather than being replaced by it: what the showroom files
            is a cut-out, so the ring and the lit floor show through
            wherever the car is not. */}
        <StudioStage uid={`frame-${index}`} />

        {failed[index] ? (
          <div className="relative grid size-full place-items-center px-6 text-center text-[13px] text-dim">
            Photography for this vehicle is on its way
          </div>
        ) : (
          /* Standing inside the ring rather than filling the frame: the car
             is centred on the arc and held to a box it closes around, so the
             circle wraps the car instead of running behind its wheels. */
          <div className={STAGE_SUBJECT}>
            <img
              ref={mainRef}
              src={frames[index]}
              alt={`${alt} — view ${index + 1} of ${count}`}
              decoding="async"
              onError={() =>
                setFailed((current) => ({ ...current, [index]: true }))
              }
              className={`max-h-full max-w-full ${CAR_IMAGE}`}
            />
          </div>
        )}

        {/* The ring stands behind the car; this is the light it throws back
            onto the bodywork, so the two read as one room rather than as a
            cut-out pasted over an arc. */}
        <StudioSpill />

        <button
          type="button"
          onClick={() => setZoomed(true)}
          aria-label="Open fullscreen viewer"
          className="absolute top-4 right-4 grid size-11 place-items-center rounded-full border border-white/15 bg-void/50 text-white backdrop-blur-md transition hover:border-lime hover:text-lime-deep"
        >
          <Expand aria-hidden="true" strokeWidth={1.7} className="size-4.5" />
        </button>

        {count > 1 ? (
          <>
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="Previous photo"
              className="absolute top-1/2 left-4 grid size-11 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-void/50 text-white backdrop-blur-md transition hover:border-lime hover:text-lime-deep"
            >
              <ChevronLeft
                aria-hidden="true"
                strokeWidth={1.7}
                className="size-5"
              />
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              aria-label="Next photo"
              className="absolute top-1/2 right-4 grid size-11 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-void/50 text-white backdrop-blur-md transition hover:border-lime hover:text-lime-deep"
            >
              <ChevronRight
                aria-hidden="true"
                strokeWidth={1.7}
                className="size-5"
              />
            </button>

            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-white/15 bg-void/50 px-3.5 py-1.5 text-[11px] text-white backdrop-blur-md tabular-nums">
              {index + 1} / {count}
            </span>
          </>
        ) : null}
      </div>

      {/* ---------- thumbnails ---------- */}
      {count > 1 ? (
        <ul className="flex snap-x gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {frames.map((image, position) => (
            <li
              key={image}
              className="w-[124px] shrink-0 snap-start sm:w-[148px]"
            >
              <button
                type="button"
                onClick={() => setIndex(position)}
                aria-label={`Show photo ${position + 1}`}
                aria-current={position === index}
                className={`aspect-4/3 w-full overflow-hidden rounded-2xl border bg-surface transition duration-300 ease-lux ${
                  position === index
                    ? "border-lime opacity-100 shadow-[0_0_0_1px_rgb(200_245_66/0.4),0_0_18px_rgb(200_245_66/0.25)]"
                    : "border-line opacity-50 hover:opacity-90"
                }`}
              >
                <img
                  src={image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="size-full object-contain p-1"
                />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {/* ---------- fullscreen viewer ---------- */}
      {zoomed ? (
        <div
          ref={lightboxRef}
          role="dialog"
          aria-modal="true"
          aria-label={`${alt} photo viewer`}
          className="fixed inset-0 z-60 flex flex-col bg-void/97 backdrop-blur-sm"
          onTouchStart={(event) => {
            touchStart.current = event.touches[0].clientX;
          }}
          onTouchEnd={onTouchEnd}
        >
          <div className="flex items-center justify-between px-5 py-4 sm:px-8">
            <span className="text-[12px] text-white/70 tabular-nums">
              {index + 1} / {count}
            </span>
            <button
              type="button"
              onClick={() => setZoomed(false)}
              aria-label="Close viewer"
              className="grid size-11 place-items-center rounded-lg border border-white/25 text-white transition hover:border-white/60 hover:bg-white/10"
            >
              <X aria-hidden="true" strokeWidth={1.8} className="size-5" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 items-center justify-center px-5 pb-8 sm:px-8">
            <img
              src={frames[index]}
              alt={`${alt} — view ${index + 1} of ${count}`}
              className="max-h-full max-w-full rounded-2xl object-contain"
            />
          </div>

          {count > 1 ? (
            <div className="flex items-center justify-center gap-4 pb-10">
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label="Previous photo"
                className="grid size-11 place-items-center rounded-lg border border-white/25 text-white transition hover:border-white/60 hover:bg-white/10"
              >
                <ChevronLeft
                  aria-hidden="true"
                  strokeWidth={1.7}
                  className="size-5"
                />
              </button>
              <button
                type="button"
                onClick={() => step(1)}
                aria-label="Next photo"
                className="grid size-11 place-items-center rounded-lg border border-white/25 text-white transition hover:border-white/60 hover:bg-white/10"
              >
                <ChevronRight
                  aria-hidden="true"
                  strokeWidth={1.7}
                  className="size-5"
                />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default VehicleGallery;
